import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, unref, type Ref, type ComputedRef } from 'vue'
import { apiClient } from '@/api/client'
import { applyReorder } from '@/utils/editorHelpers'
import type {
  PageResponse as Page,
  ComponentInstanceResponse as ComponentInstance,
  PageCreateRequest as CreatePageData,
  PageUpdateRequest as UpdatePageData,
} from '@aannemerplus/types'

export type { Page, ComponentInstance }

// Query keys
export const pageKeys = {
  all: ['pages'] as const,
  lists: () => [...pageKeys.all, 'list'] as const,
  list: (projectSlug: string) => [...pageKeys.lists(), { projectSlug }] as const,
  details: () => [...pageKeys.all, 'detail'] as const,
  detail: (id: number) => [...pageKeys.details(), id] as const,
  components: (pageId: number) => [...pageKeys.all, 'components', pageId] as const,
}

// API functions
const fetchPages = async (projectSlug: string): Promise<Page[]> => {
  // Pages come from website config, not a direct pages endpoint
  const response = await apiClient.getWebsiteConfig(projectSlug)
  const websiteConfig = response.data as any
  if (websiteConfig?.content?.[0]?.pages) {
    return websiteConfig.content[0].pages.map((page: any) => ({
      id: page.id,
      title: page.Title,
      slug: page.slug,
      layoutType: page.layoutType,
      projectId: page.projectId || 0,
      status: 'published',
      sections: page.Main || [],
      createdAt: page.createdAt,
      updatedAt: page.updatedAt
    }))
  }
  return []
}

const fetchPage = async (id: number): Promise<Page> => {
  const response = await apiClient.getPageById(id.toString())
  const pageData = response.data as any
  return {
    id: pageData.id,
    title: pageData.title,
    slug: pageData.slug,
    layoutType: pageData.layoutType,
    projectId: pageData.projectId || pageData.project_id,
    status: pageData.status || 'draft',
    sections: pageData.sections || [],
    createdAt: pageData.createdAt || pageData.created_at,
    updatedAt: pageData.updatedAt || pageData.updated_at
  }
}

const fetchPageComponents = async (pageId: number): Promise<ComponentInstance[]> => {
  const response = await apiClient.getPageComponents(pageId.toString())
  const componentData = response.data as any[]
  if (!Array.isArray(componentData)) return []
  return componentData.map((comp: any) => ({
    id: comp.id,
    componentType: comp.componentType,
    contentData: comp.contentData,
    position: comp.position,
    parentInstanceId: comp.parentInstanceId,
    pageId: comp.pageId,
    children: comp.children?.map((child: any) => ({
      id: child.id,
      componentType: child.componentType,
      contentData: child.contentData,
      position: child.position,
      parentInstanceId: child.parentInstanceId,
      pageId: child.pageId
    })) || []
  }))
}

const createPage = async (projectId: number, pageData: CreatePageData): Promise<Page> => {
  const response = await apiClient.createPage(projectId.toString(), {
    ...pageData,
    projectId
  })
  const responseData = response.data as any
  return {
    id: responseData.id,
    title: responseData.title,
    slug: responseData.slug,
    layoutType: responseData.layoutType,
    projectId: responseData.projectId || projectId,
    status: responseData.status || 'draft',
    sections: responseData.sections || [],
    createdAt: responseData.createdAt,
    updatedAt: responseData.updatedAt
  }
}

const updatePage = async ({ id, ...updates }: UpdatePageData & { id: number }): Promise<Page> => {
  const response = await apiClient.updatePage(id.toString(), updates)
  const responseData = response.data as any
  return {
    id: responseData.id,
    title: responseData.title,
    slug: responseData.slug,
    layoutType: responseData.layoutType,
    projectId: responseData.projectId || responseData.project_id,
    status: responseData.status || 'draft',
    sections: responseData.sections || [],
    createdAt: responseData.createdAt || responseData.created_at,
    updatedAt: responseData.updatedAt || responseData.updated_at
  }
}

const deletePage = async (id: number): Promise<void> => {
  await apiClient.deletePage(id.toString())
}

const publishPage = async (pageId: number, versionId?: string): Promise<any> => {
  const versionPath = versionId || 'latest'
  const response = await apiClient.publishPageVersion(pageId.toString(), versionPath)
  return response.data
}

// Component operations
const createComponent = async (componentData: {
  pageId: number
  componentType: string
  contentData: any
  position: number
  parentInstanceId?: number
}): Promise<ComponentInstance> => {
  const response = await apiClient.createPageComponent(componentData.pageId.toString(), {
    componentType: componentData.componentType,
    contentData: componentData.contentData,
    position: componentData.position,
    parentInstanceId: componentData.parentInstanceId
  })
  const responseData = response.data as any
  return {
    id: responseData.id,
    componentType: responseData.componentType,
    contentData: responseData.contentData,
    position: responseData.position,
    parentInstanceId: responseData.parentInstanceId,
    pageId: componentData.pageId
  }
}

const updateComponent = async ({ id, pageId, ...updates }: Partial<ComponentInstance> & { id: number; pageId?: number }): Promise<ComponentInstance> => {
  const response = await apiClient.updateComponentInstance(id.toString(), {
    contentData: updates.contentData,
    position: updates.position
  })
  const responseData = response.data as any
  return {
    id: responseData.id,
    componentType: responseData.componentType,
    contentData: responseData.contentData,
    position: responseData.position,
    parentInstanceId: responseData.parentInstanceId,
    pageId: pageId || responseData.pageId
  }
}

const deleteComponent = async (id: number): Promise<void> => {
  await apiClient.deleteComponentInstance(id.toString())
}

export interface ReorderComponentsVariables {
  pageId: number
  /** null = the page's root sections */
  parentInstanceId: number | null
  /** ALL siblings under that parent, in their new order */
  orderedIds: number[]
}

// One transactional call; the backend rejects anything that isn't exactly the sibling set.
const reorderComponents = async ({ pageId, parentInstanceId, orderedIds }: ReorderComponentsVariables): Promise<void> => {
  await apiClient.reorderPageComponents(pageId.toString(), { parentInstanceId, orderedIds })
}

// Server-side deep copy (instance + whole subtree) placed right after the original.
const duplicateComponent = async (componentId: number): Promise<ComponentInstance> => {
  const response = await apiClient.duplicateComponentInstance(componentId.toString())
  const d = response.data as any
  return {
    id: d.id,
    componentType: d.componentType,
    contentData: d.contentData,
    position: d.position,
    parentInstanceId: d.parentInstanceId,
    pageId: d.pageId
  }
}

// Query hooks
export const usePagesQuery = (projectSlug: string | Ref<string> | ComputedRef<string>) => {
  return useQuery({
    queryKey: computed(() => pageKeys.list(unref(projectSlug))),
    queryFn: () => fetchPages(unref(projectSlug)),
    enabled: computed(() => !!unref(projectSlug)),
  })
}

export const usePageQuery = (pageId: number | Ref<number> | ComputedRef<number>) => {
  return useQuery({
    queryKey: computed(() => pageKeys.detail(unref(pageId))),
    queryFn: () => fetchPage(unref(pageId)),
    enabled: computed(() => !!unref(pageId)),
  })
}

export const usePageComponentsQuery = (pageId: number | Ref<number> | ComputedRef<number>) => {
  return useQuery({
    queryKey: computed(() => pageKeys.components(unref(pageId))),
    queryFn: () => fetchPageComponents(unref(pageId)),
    enabled: computed(() => !!unref(pageId)),
  })
}

// Mutation hooks
export const useCreatePageMutation = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ projectId, ...pageData }: CreatePageData & { projectId: number }) => 
      createPage(projectId, pageData),
    onSuccess: (newPage) => {
      // Invalidate pages list for the project - we need project slug, not ID
      // For now, invalidate all lists to be safe
      queryClient.invalidateQueries({ queryKey: pageKeys.lists() })
      
      // Add the new page to cache
      queryClient.setQueryData(pageKeys.detail(newPage.id), newPage)
    },
  })
}

export const useUpdatePageMutation = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: updatePage,
    onSuccess: (updatedPage) => {
      // Update the page in cache
      queryClient.setQueryData(pageKeys.detail(updatedPage.id), updatedPage)
      
      // Invalidate all pages lists since we need project slug to target specific list
      queryClient.invalidateQueries({ queryKey: pageKeys.lists() })
    },
  })
}

export const useDeletePageMutation = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: deletePage,
    onSuccess: (_, deletedId) => {
      // Remove from all pages lists (we don't know the project ID here)
      queryClient.removeQueries({ queryKey: pageKeys.detail(deletedId) })
      queryClient.removeQueries({ queryKey: pageKeys.components(deletedId) })
      
      // Invalidate all pages lists to be safe
      queryClient.invalidateQueries({ queryKey: pageKeys.lists() })
    },
  })
}

export const usePublishPageMutation = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ pageId, versionId }: { pageId: number; versionId?: string }) =>
      publishPage(pageId, versionId),
    onSuccess: (_, { pageId }) => {
      // Invalidate page data to get updated published status
      queryClient.invalidateQueries({ queryKey: pageKeys.detail(pageId) })
    },
  })
}

// Component mutation hooks
export const useCreateComponentMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createComponent,
    onSuccess: (_newComponent, variables) => {
      // Invalidate to refetch full hierarchy (handles both root and nested components)
      queryClient.invalidateQueries({ queryKey: pageKeys.components(variables.pageId) })
    },
  })
}

export const useUpdateComponentMutation = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: updateComponent,
    onSuccess: (_updatedComponent, variables) => {
      const pageId = variables.pageId || _updatedComponent.pageId
      if (pageId) {
        // Invalidate to refetch full hierarchy (handles both root and nested components)
        queryClient.invalidateQueries({ queryKey: pageKeys.components(pageId) })
      }
    },
  })
}

export const useDeleteComponentMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ componentId, pageId }: { componentId: number; pageId: number }) =>
      deleteComponent(componentId),
    onSuccess: (_, { pageId }) => {
      // Invalidate to refetch full hierarchy (handles both root and nested components)
      queryClient.invalidateQueries({ queryKey: pageKeys.components(pageId) })
    },
  })
}

export const useReorderComponentsMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: reorderComponents,
    // Optimistic: show the new order immediately, roll back if the server refuses.
    onMutate: async ({ pageId, parentInstanceId, orderedIds }) => {
      const key = pageKeys.components(pageId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<ComponentInstance[]>(key)
      if (previous) {
        queryClient.setQueryData(key, applyReorder(previous, parentInstanceId, orderedIds))
      }
      return { previous }
    },
    onError: (_error, { pageId }, context) => {
      if (context?.previous) queryClient.setQueryData(pageKeys.components(pageId), context.previous)
    },
    onSettled: (_data, _error, { pageId }) => {
      queryClient.invalidateQueries({ queryKey: pageKeys.components(pageId) })
    },
  })
}

export const useDuplicateComponentMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ componentId }: { componentId: number; pageId: number }) => duplicateComponent(componentId),
    onSuccess: (_copy, { pageId }) => {
      queryClient.invalidateQueries({ queryKey: pageKeys.components(pageId) })
    },
  })
}